import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged, ValueOf } from 'type-fest';
import { SystemMutableEntity as SME } from '../../common';
import { UniqueShortId } from '../../common/id';
import type { SystemCommandContext } from '../common';
import { IdentityLevel } from '../identityLevel';
import { IdentityNode } from '../identityNode';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(18);

const _ClusterType = {
  Behavior: 'Behavior',
  Verified: 'Verified',
} as const;
type _ClusterType = ValueOf<typeof _ClusterType>;

export type IdentityCluster = SME & {
  id: IdentityCluster.Id;
  projectId: Project.Id;
  clusterType: _ClusterType;
  nodeIds: IdentityNode.Id[];
};

export namespace IdentityCluster {
  export const Type = _ClusterType;
  export type Type = _ClusterType;

  export type Id = Tagged<UniqueShortId, 'identityClusterId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  const isClusterType = (value: string): value is _ClusterType => {
    return (Object.values(_ClusterType) as string[]).includes(value);
  };

  export type Props = {
    id: string;
    projectId: string;
    clusterType: string;
    nodeIds: string[];
  } & SME.Props;
  export const Construct = (props: Props): IdentityCluster => {
    if (!isClusterType(props.clusterType)) {
      throw new Error(`Invalid ClusterType: ${props.clusterType}`);
    }
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      clusterType: props.clusterType,
      nodeIds: props.nodeIds.map(IdentityNode.Id.FromString),
      ...SME.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<IdentityCluster.Props, 'id' | 'projectId' | 'createdAt' | 'updatedAt' | 'nodeIds'> & {
      nodeIds: IdentityNode.Id[];
    };
    export type Params = {
      context: SystemCommandContext;
      props: Create.Props;
    };
  }
  export const Create = (params: Create.Params): IdentityCluster => {
    const { context, props } = params;
    const { projectId } = context;
    return Construct({
      id: Id.Generate(),
      projectId,
      ...props,
      ...SME.Create({ date: context.now }),
    });
  };

  export namespace Command {
    export const Merge = async (params: {
      context: SystemCommandContext;
      nodesWithLevels: { node: IdentityNode; level: IdentityLevel }[];
      getClusters: (params: {
        projectId: Project.Id;
        nodeIds: IdentityNode.Id[];
        clusterType: IdentityCluster.Type;
      }) => Promise<IdentityCluster[]>;
    }) => {
      const { context, nodesWithLevels, getClusters } = params;
      const { projectId } = context;
      const behaviorNodes = nodesWithLevels.filter((n) => IdentityLevel.isBehaviorLevel(n.level)).map((n) => n.node);
      const verifiedNodes = nodesWithLevels.filter((n) => IdentityLevel.isVerifiedLevel(n.level)).map((n) => n.node);

      return {
        behaviorCluster: _Merge({
          context,
          clusterType: IdentityCluster.Type.Behavior,
          nodeIds: behaviorNodes.map((n) => n.id),
          clustersToMerge: await getClusters({
            projectId,
            nodeIds: behaviorNodes.map((n) => n.id),
            clusterType: IdentityCluster.Type.Behavior,
          }),
        }),
        verifiedCluster: _Merge({
          context,
          clusterType: IdentityCluster.Type.Verified,
          nodeIds: verifiedNodes.map((n) => n.id),
          clustersToMerge: await getClusters({
            projectId,
            nodeIds: verifiedNodes.map((n) => n.id),
            clusterType: IdentityCluster.Type.Verified,
          }),
        }),
      };
    };

    export const _Merge = (params: {
      context: SystemCommandContext;
      clusterType: IdentityCluster.Type;
      nodeIds: IdentityNode.Id[];
      clustersToMerge: IdentityCluster[];
    }): {
      toSave: IdentityCluster | undefined;
      toDelete: IdentityCluster[];
    } => {
      const { context, clusterType, nodeIds, clustersToMerge } = params;
      const { now } = context;

      if (nodeIds.length === 0) return { toSave: undefined, toDelete: [] };

      if (clustersToMerge.length === 0) {
        // 新規クラスタを作成
        const newCluster = IdentityCluster.Create({
          context,
          props: {
            clusterType,
            nodeIds,
          },
        });
        return { toSave: newCluster, toDelete: [] };
      }

      // query の sort により最も古いクラスタが選択される
      const primary = clustersToMerge[0];
      const mergedNodeIds = [...new Set([...clustersToMerge.flatMap((c) => c.nodeIds), ...nodeIds])];

      // プライマリクラスタを更新
      const updatedPrimaryCluster = {
        ...primary,
        nodeIds: mergedNodeIds,
        ...SME.Update({ cur: primary, date: now }),
      };

      return {
        toSave: updatedPrimaryCluster,
        toDelete: clustersToMerge.slice(1),
      };
    };
  }
}
